import React, {
  useEffect, useMemo, useRef, useState,
} from "react";

import {
  ArrowRightOutlined, LeftOutlined, LoadingOutlined, RightOutlined,
} from "@ant-design/icons";
import { Input, Table } from "antd";
import moment from "moment";
import { toast } from "react-toastify";

import DatePicker from "../../Common/DatePicker/DatePicker";
import StyledScrollbar from "../../Common/UIComponents/StyledScrollbar";
import InvoiceHeader from "./InvoiceHeader";

import { validateEmail } from "../../Common/utils/validationUtils";
import { formatInvoiceAmount } from "./utils";

import { previewSubmissionInvoice } from "../../../actions/invoice";

import { ReactComponent as CalendarLineIcon } from "../../../assets/images/subscriptions/calendar-line.svg";
import { ReactComponent as CheckboxCircleFillIcon } from "../../../assets/images/subscriptions/checkbox-circle-fill.svg";

import "./SetInvoiceDetails.css";

const STEPS = ["Select submissions", "Set invoice details", "Preview & save"];
const ACTIVE_STEP = 1;

const getStepStatus = (index) => {
  if (index < ACTIVE_STEP) return "complete";
  if (index === ACTIVE_STEP) return "active";
  return "upcoming";
};

const toAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

const MAX_MARKUP_PERCENT = 10000;

const sanitizeMarkup = (value) => {
  const [whole, ...rest] = String(value).replace(/[^\d.]/g, "").split(".");
  if (!rest.length) return whole;
  return `${whole}.${rest.join("").slice(0, 2)}`;
};

const markupExceedsMax = (value) => toAmount(value) > MAX_MARKUP_PERCENT;

const normalizeMarkup = (value) => {
  const sanitized = sanitizeMarkup(value);
  if (!sanitized || sanitized === ".") return "0";
  const trimmed = sanitized.endsWith(".") ? sanitized.slice(0, -1) : sanitized;
  return trimmed.replace(/^0+(?=\d)/, "") || "0";
};

const plainAmount = (value) => {
  if (!Number.isFinite(value)) return "";
  const amount = value.toFixed(2);
  return /e/i.test(amount) ? "" : amount;
};

export default function SetInvoiceDetails({ onBack, onPreview, submissions = [] }) {
  const [invoiceName, setInvoiceName] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(moment());
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [stateRegion, setStateRegion] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");
  const [markupPercent, setMarkupPercent] = useState("0");
  const [header, setHeader] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [previewing, setPreviewing] = useState(false);
  const previewRequestRef = useRef(0);

  useEffect(() => () => {
    previewRequestRef.current += 1;
  }, []);

  const leaveDetails = () => {
    previewRequestRef.current += 1;
    setPreviewing(false);
    onBack();
  };

  const clearFieldError = (key, value) => {
    if (fieldErrors[key] && String(value ?? "").trim()) {
      setFieldErrors((prev) => ({ ...prev, [key]: false }));
    }
  };

  const renderFieldError = (key, message = "Invalid Input") => (
    fieldErrors[key] ? <span className="SetInvoiceDetails__FieldError">{message}</span> : null
  );

  const currency = submissions.find((row) => row.currency)?.currency || "USD";
  const markupValue = sanitizeMarkup(markupPercent);
  const markupRate = Math.max(0, toAmount(markupValue)) / 100;

  const pricedSubmissions = useMemo(() => submissions.map((row) => {
    const baseAmount = toAmount(row.amount);
    const markupAmount = baseAmount * markupRate;
    return {
      ...row,
      baseAmount,
      markupAmount,
      finalAmount: baseAmount + markupAmount,
    };
  }), [markupRate, submissions]);

  const sampleCount = pricedSubmissions.reduce((sum, row) => sum + toAmount(row.samples), 0);
  const baseTotal = pricedSubmissions.reduce((sum, row) => sum + row.baseAmount, 0);
  const markupTotal = pricedSubmissions.reduce((sum, row) => sum + row.markupAmount, 0);
  const invoiceTotal = baseTotal + markupTotal;
  const cityLine = [city, stateRegion].filter(Boolean).join(", ");
  const cityPostalLine = [cityLine, postalCode].filter(Boolean).join(" ");
  const billToLines = [
    { key: "address1", text: addressLine1 },
    { key: "address2", text: addressLine2 },
    { key: "cityPostal", text: cityPostalLine },
    { key: "country", text: country },
    { key: "email", text: clientEmail },
  ].filter((line) => line.text);
  const summaryTitle = [clientName, invoiceDate ? invoiceDate.format("DD/MM") : "", "Invoice"]
    .filter(Boolean)
    .join(" ");
  const markupLabel = `${markupValue || "0"}%`;
  const formatMoney = (value) => {
    const amount = plainAmount(value);
    return amount ? formatInvoiceAmount(amount, currency) : "";
  };
  const pricingColumns = [
    { title: "Submission Name", dataIndex: "name", key: "name" },
    {
      title: "Samples", dataIndex: "samples", key: "samples", width: 90,
    },
    {
      title: "Base Amount",
      dataIndex: "baseAmount",
      key: "baseAmount",
      render: (amount) => formatMoney(amount),
    },
    {
      title: "Markup",
      key: "markup",
      width: 90,
      render: () => markupLabel,
    },
    {
      title: "Markup Amount",
      dataIndex: "markupAmount",
      key: "markupAmount",
      render: (amount) => formatMoney(amount),
    },
    {
      title: "Final Amount",
      dataIndex: "finalAmount",
      key: "finalAmount",
      render: (amount) => (
        <span className="SetInvoiceDetails__FinalAmount">
          {formatMoney(amount)}
        </span>
      ),
    },
  ];

  return (
    <div className="SetInvoiceDetails">
      <header className="SetInvoiceDetails__Header">
        <div className="SetInvoiceDetails__TitleBlock">
          <button
            type="button"
            className="SetInvoiceDetails__Back"
            onClick={leaveDetails}
            aria-label="Back to select submissions"
          >
            <LeftOutlined />
          </button>
          <div>
            <h1 className="SetInvoiceDetails__Title">Set Invoice details</h1>
            <p className="SetInvoiceDetails__Description">
              Add the invoice, customer, branding, and pricing details before previewing.
            </p>
          </div>
        </div>
        <ol className="SetInvoiceDetails__Steps">
          {STEPS.map((label, index) => {
            const status = getStepStatus(index);

            return (
              <li key={label} className={`is-${status}`}>
                <span className="SetInvoiceDetails__Step">
                  <span
                    className={`SetInvoiceDetails__StepIcon SetInvoiceDetails__StepIcon--${status}`}
                    aria-hidden
                  >
                    {status === "complete" ? <CheckboxCircleFillIcon /> : index + 1}
                  </span>
                  <span>{label}</span>
                </span>
                {index < STEPS.length - 1 && (
                  <RightOutlined className="SetInvoiceDetails__StepChevron" />
                )}
              </li>
            );
          })}
        </ol>
      </header>

      <div className="SetInvoiceDetails__Body">
        <StyledScrollbar
          className="SetInvoiceDetails__FormScroll"
          noScrollX
          disableTrackYWidthCompensation
          disableTracksWidthCompensation
        >
          <div className="SetInvoiceDetails__Form">
            <section className="SetInvoiceDetails__Section">
              <div className="SetInvoiceDetails__SectionHeading">
                <span className="SetInvoiceDetails__SectionMark" aria-hidden />
                <div>
                  <h2>Invoice</h2>
                  <p>Add the basic details and optional branding for this invoice.</p>
                </div>
              </div>

              <InvoiceHeader onChange={setHeader} />

              <div className="SetInvoiceDetails__Fields">
                <label className="SetInvoiceDetails__Field">
                  <span>Invoice number</span>
                  <Input
                    value=""
                    readOnly
                    placeholder="Invoice number will be generated on save"
                  />
                </label>
                <label className="SetInvoiceDetails__Field">
                  <span>Invoice Name (optional)</span>
                  <Input
                    value={invoiceName}
                    onChange={(event) => setInvoiceName(event.target.value)}
                    placeholder="Give a name to your invoice"
                  />
                </label>
                <label className="SetInvoiceDetails__Field">
                  <span>Invoice date</span>
                  <DatePicker
                    mode="single"
                    allowClear={false}
                    showClearBtn={false}
                    value={invoiceDate ? invoiceDate.format("MMM D, YYYY") : ""}
                    onChange={(dateStr) => {
                      const nextDate = dateStr ? moment(dateStr, "MMM D, YYYY") : null;
                      setInvoiceDate(nextDate && nextDate.isValid() ? nextDate : null);
                      clearFieldError("invoiceDate", dateStr);
                    }}
                    getPopupContainer={() => document.body}
                    datePickerDisplayProps={{
                      displayClassName: `SetInvoiceDetails__Date${fieldErrors.invoiceDate ? " SetInvoiceDetails__Date--error" : ""}`,
                      showText: true,
                      emptyPlaceholder: "Select date",
                      customCalendarIcon: (
                        <CalendarLineIcon className="SetInvoiceDetails__DateIcon" aria-hidden />
                      ),
                    }}
                  />
                  {renderFieldError("invoiceDate")}
                </label>
              </div>
            </section>

            <section className="SetInvoiceDetails__Section">
              <div className="SetInvoiceDetails__SectionHeading">
                <span className="SetInvoiceDetails__SectionMark" aria-hidden />
                <div>
                  <h2>Client</h2>
                  <p>Enter the billing details of the customer receiving this invoice.</p>
                </div>
              </div>
              <div className="SetInvoiceDetails__Fields">
                <label className="SetInvoiceDetails__Field">
                  <span>Client&apos;s Name</span>
                  <Input
                    value={clientName}
                    status={fieldErrors.clientName ? "error" : ""}
                    onChange={(event) => {
                      setClientName(event.target.value);
                      clearFieldError("clientName", event.target.value);
                    }}
                    placeholder="Enter company or client name"
                  />
                  {renderFieldError("clientName")}
                </label>
                <label className="SetInvoiceDetails__Field SetInvoiceDetails__Field--email">
                  <span>Client&apos;s Email</span>
                  <Input
                    value={clientEmail}
                    status={fieldErrors.clientEmail ? "error" : ""}
                    onChange={(event) => {
                      const nextEmail = event.target.value;
                      setClientEmail(nextEmail);
                      if (fieldErrors.clientEmail && validateEmail(nextEmail.trim())) {
                        setFieldErrors((prev) => ({ ...prev, clientEmail: false }));
                      }
                    }}
                    placeholder="example@email.com"
                  />
                  {renderFieldError("clientEmail", clientEmail.trim() ? "Invalid email" : "Invalid Input")}
                </label>
                <label className="SetInvoiceDetails__Field">
                  <span>Client&apos;s Address line 1</span>
                  <Input
                    value={addressLine1}
                    status={fieldErrors.addressLine1 ? "error" : ""}
                    onChange={(event) => {
                      setAddressLine1(event.target.value);
                      clearFieldError("addressLine1", event.target.value);
                    }}
                    placeholder="Street address and building number"
                  />
                  {renderFieldError("addressLine1")}
                </label>
                <label className="SetInvoiceDetails__Field">
                  <span>Client&apos;s Address line 2 (Optional)</span>
                  <Input
                    value={addressLine2}
                    onChange={(event) => setAddressLine2(event.target.value)}
                    placeholder="Suite, floor, unit, or department"
                  />
                </label>
                <label className="SetInvoiceDetails__Field">
                  <span>City</span>
                  <Input
                    value={city}
                    status={fieldErrors.city ? "error" : ""}
                    onChange={(event) => {
                      setCity(event.target.value);
                      clearFieldError("city", event.target.value);
                    }}
                    placeholder="Enter City"
                  />
                  {renderFieldError("city")}
                </label>
                <label className="SetInvoiceDetails__Field">
                  <span>State / region</span>
                  <Input
                    value={stateRegion}
                    status={fieldErrors.stateRegion ? "error" : ""}
                    onChange={(event) => {
                      setStateRegion(event.target.value);
                      clearFieldError("stateRegion", event.target.value);
                    }}
                    placeholder="Enter State"
                  />
                  {renderFieldError("stateRegion")}
                </label>
                <label className="SetInvoiceDetails__Field">
                  <span>Postal code</span>
                  <Input
                    value={postalCode}
                    status={fieldErrors.postalCode ? "error" : ""}
                    onChange={(event) => {
                      setPostalCode(event.target.value);
                      clearFieldError("postalCode", event.target.value);
                    }}
                    placeholder="Postal code"
                  />
                  {renderFieldError("postalCode")}
                </label>
                <label className="SetInvoiceDetails__Field">
                  <span>Country</span>
                  <Input
                    value={country}
                    status={fieldErrors.country ? "error" : ""}
                    onChange={(event) => {
                      setCountry(event.target.value);
                      clearFieldError("country", event.target.value);
                    }}
                    placeholder="Enter Country"
                  />
                  {renderFieldError("country")}
                </label>
              </div>
            </section>

            <section className="SetInvoiceDetails__Section">
              <div className="SetInvoiceDetails__SectionHeading">
                <span className="SetInvoiceDetails__SectionMark" aria-hidden />
                <div>
                  <h2>Pricing</h2>
                  <p>Review the selected submissions and apply an optional markup.</p>
                </div>
              </div>
              <label className="SetInvoiceDetails__Field SetInvoiceDetails__Field--markup">
                <span>Markup Percentage</span>
                <Input
                  value={markupValue}
                  onChange={(event) => {
                    const next = sanitizeMarkup(event.target.value);
                    if (markupExceedsMax(next) && !markupExceedsMax(markupValue)) {
                      toast.error("Markup percentage cannot exceed 10000%");
                    }
                    setMarkupPercent(next);
                  }}
                  onBlur={() => setMarkupPercent(normalizeMarkup)}
                  addonAfter="%"
                />
              </label>
              <Table
                className="SetInvoiceDetails__Table"
                rowKey="id"
                columns={pricingColumns}
                dataSource={pricedSubmissions}
                pagination={false}
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>{sampleCount}</Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>{formatMoney(baseTotal)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>{markupLabel}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>{formatMoney(markupTotal)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>
                      <span className="SetInvoiceDetails__FinalAmount">
                        {formatMoney(invoiceTotal)}
                      </span>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
            </section>
          </div>
        </StyledScrollbar>

        <div className="SetInvoiceDetails__SummaryFrame">
          <aside className="SetInvoiceDetails__Summary">
            <StyledScrollbar
              className="SetInvoiceDetails__SummaryScroll"
              noScrollX
              disableTrackYWidthCompensation
              disableTracksWidthCompensation
            >
              <div className="SetInvoiceDetails__SummaryBody">
                <h2 className="SetInvoiceDetails__SummaryTitle">{summaryTitle}</h2>
                <div className="SetInvoiceDetails__BillTo">
                  <span className="SetInvoiceDetails__BillToLabel">Bill to</span>
                  <p className="SetInvoiceDetails__BillToName">{clientName}</p>
                  {billToLines.map((line) => (
                    <p key={line.key}>{line.text}</p>
                  ))}
                </div>
                <p className="SetInvoiceDetails__SelectedCount">
                  {`${pricedSubmissions.length} selected submission${pricedSubmissions.length === 1 ? "" : "s"}`}
                </p>
                <ul className="SetInvoiceDetails__SelectedList">
                  {pricedSubmissions.map((row) => (
                    <li key={row.id}>
                      <span>{row.name}</span>
                      <span>{formatMoney(row.baseAmount)}</span>
                    </li>
                  ))}
                </ul>
                <dl className="SetInvoiceDetails__Totals">
                  <div>
                    <dt>Base Total</dt>
                    <dd>{formatMoney(baseTotal)}</dd>
                  </div>
                  <div>
                    <dt>{`Markup amount (${markupLabel})`}</dt>
                    <dd>{formatMoney(markupTotal)}</dd>
                  </div>
                  <div className="is-total">
                    <dt>Invoice Total</dt>
                    <dd>{formatMoney(invoiceTotal)}</dd>
                  </div>
                </dl>
              </div>
            </StyledScrollbar>
            <button
              type="button"
              className="SetInvoiceDetails__Preview"
              disabled={previewing || pricedSubmissions.length === 0}
              onClick={async () => {
                if (previewing) return;
                if (pricedSubmissions.length === 0) {
                  toast.error("Select at least one submission");
                  return;
                }
                const nextErrors = {
                  invoiceDate: !invoiceDate,
                  clientName: !clientName.trim(),
                  clientEmail: !validateEmail(clientEmail.trim()),
                  addressLine1: !addressLine1.trim(),
                  city: !city.trim(),
                  stateRegion: !stateRegion.trim(),
                  postalCode: !postalCode.trim(),
                  country: !country.trim(),
                };
                setFieldErrors(nextErrors);
                if (Object.values(nextErrors).some(Boolean)) {
                  toast.error("Invalid input");
                  return;
                }

                if (markupExceedsMax(markupValue)) {
                  toast.error("Markup percentage cannot exceed 10000%");
                  return;
                }

                const amount = plainAmount(invoiceTotal);
                if (!amount) {
                  toast.error("Enter a valid markup percentage");
                  return;
                }

                const previewPayload = {
                  submissionIds: pricedSubmissions.map((row) => row.id),
                  invoiceDate: invoiceDate.format("YYYY-MM-DD"),
                  amount,
                  clientName: clientName.trim(),
                  address1: addressLine1.trim(),
                  address2: addressLine2.trim(),
                  city: city.trim(),
                  state: stateRegion.trim(),
                  postalCode: postalCode.trim(),
                  country: country.trim(),
                  clientEmail: clientEmail.trim(),
                  headerFile: header?.imageUrl,
                };

                const requestId = previewRequestRef.current + 1;
                previewRequestRef.current = requestId;
                setPreviewing(true);
                const response = await previewSubmissionInvoice(previewPayload);
                if (previewRequestRef.current !== requestId) return;
                setPreviewing(false);

                if (!response.success) {
                  toast.error(response.message || "Unable to preview this invoice.");
                  return;
                }

                onPreview({
                  title: summaryTitle,
                  previewUrl: URL.createObjectURL(response.blob),
                  filename: response.filename,
                  previewPayload,
                  amount,
                  invoiceDate: invoiceDate.format("YYYY-MM-DD"),
                  invoiceName: invoiceName.trim(),
                });
              }}
            >
              <span>{previewing ? "Previewing..." : "Preview Invoice"}</span>
              {previewing ? <LoadingOutlined /> : <ArrowRightOutlined aria-hidden />}
            </button>
          </aside>
        </div>
      </div>
    </div>
  );
}
